import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import SheetImposition from '@/components/SheetImposition';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { readPageCache, writePageCache } from '@/lib/pageCache';
import useRefreshOnFocus from '@/lib/useRefreshOnFocus';
import {
  DEFAULT_PRODUCTION_PRIORITY,
  DEFAULT_PRODUCTION_STATUS,
  NEXT_PRODUCTION_STATUS,
  PRODUCTION_PRIORITIES,
  PRODUCTION_PRIORITY_LABELS,
  PRODUCTION_PRIORITY_ORDER,
  PRODUCTION_STATUSES,
  PRODUCTION_STATUS_LABELS,
} from '@/constants/production';
import '@/pages/Products.css';
import '@/pages/Clients.css';
import '@/components/SheetImposition.css';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-AR');
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString('es-AR')} ${date.toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('es-AR');

const formatSize = (width, height, unit = 'cm') => {
  if (!width && !height) return '-';
  return `${formatNumber(width)} x ${formatNumber(height)} ${unit}`;
};

const getProductionImpositionGroups = (order) => {
  if (Array.isArray(order?.imposition_groups) && order.imposition_groups.length > 0) {
    return order.imposition_groups;
  }
  if (!order?.imposition?.label) return [];
  return [{
    key: 'principal',
    title: 'Elemento Numero 1',
    subtitle: 'Trabajo',
    kind: 'simple',
    item_width: order.imposition?.pieces?.[0]?.w || 0,
    item_height: order.imposition?.pieces?.[0]?.h || 0,
    sheets: order.imposition?.sheets_needed,
    mother_sheets: order.imposition?.mother_sheets_needed,
    items_per_sheet: order.imposition?.items_per_sheet,
    imposition: order.imposition,
  }];
};

const ProductionImpositionGroup = ({ group, index }) => {
  const imposition = group.imposition || {};
  const config = imposition.config || {};
  const pieces = imposition.pieces || [];
  const itemWidth = Number(group.item_width || pieces[0]?.w || 0);
  const itemHeight = Number(group.item_height || pieces[0]?.h || 0);
  const sourceSheet = imposition.source_sheet || {};
  const generatedSheet = imposition.generated_sheet || {};
  const sheetWidth = group.sheet_width || sourceSheet.width;
  const sheetHeight = group.sheet_height || sourceSheet.height;
  const generatedWidth = group.generated_width || generatedSheet.width || config.sheetWidth;
  const generatedHeight = group.generated_height || generatedSheet.height || config.sheetHeight;

  return (
    <div className="production-work-sheet" data-testid={`production-imposition-group-${group.key || index}`}>
      <div className="production-work-sheet-header">
        <strong>{group.title || `Elemento Numero ${index + 1}`}</strong>
        <span>{group.subtitle || group.kind || 'Trabajo'}</span>
      </div>

      <div className="production-work-sheet-grid">
        <div className="production-work-sheet-panel">
          <div className="production-work-row"><strong>Material:</strong> <span>{group.paper || '-'}</span></div>
          {group.grammage ? <div className="production-work-row"><strong>Gramaje:</strong> <span>{formatNumber(group.grammage)} gr</span></div> : null}
          {group.mother_sheets ? <div className="production-work-row"><strong>Hojas:</strong> <span>{formatNumber(group.mother_sheets)}</span></div> : null}
          {group.spoilage_mother_sheets ? <div className="production-work-row"><strong>Desperdicio:</strong> <span>{formatNumber(group.spoilage_mother_sheets)} hojas</span></div> : null}
          <div className="production-work-row"><strong>Medidas:</strong> <span>{formatSize(itemWidth, itemHeight)}</span></div>
          <div className="production-work-row"><strong>Detalle:</strong> <span>{group.subtitle || '-'}</span></div>

          <div className="production-work-diagram-label">Diagrama de Hoja</div>
          <div className="production-sheet-outline">
            <span className="production-sheet-size left">{formatNumber(sheetHeight || config.sourceCut?.sourceSheet?.height || 0)}</span>
            <div className="production-sheet-box">
              <div></div>
              <div></div>
            </div>
            <span className="production-sheet-size bottom">{formatNumber(sheetWidth || config.sourceCut?.sourceSheet?.width || 0)}</span>
          </div>
        </div>

        <div className="production-work-sheet-panel">
          <div className="production-work-row"><strong>Formato Hoja:</strong> <span>{formatSize(sheetWidth, sheetHeight)}</span></div>
          <div className="production-work-row"><strong>Formato Pliego:</strong> <span>{formatSize(generatedWidth, generatedHeight)}</span></div>
          <div className="production-work-row"><strong>Boc/Pie/Pag:</strong> <span>{formatNumber(group.items_per_sheet || imposition.items_per_sheet || 0)}</span></div>
          <div className="production-work-row"><strong>Cantidad Pliegos:</strong> <span>{formatNumber(group.sheets || imposition.sheets_needed || 0)}</span></div>
          {group.pages ? <div className="production-work-row"><strong>Paginas:</strong> <span>{formatNumber(group.pages)}</span></div> : null}
          {group.pages_per_sheet ? <div className="production-work-row"><strong>Paginas por pliego:</strong> <span>{formatNumber(group.pages_per_sheet)}</span></div> : null}
          <div className="production-work-mini-title">Impresion:</div>
          <div className="production-work-row"><strong>Planchas:</strong> <span>{formatNumber(group.plates || 0)}</span></div>
          <div className="production-work-row"><strong>Colores:</strong> <span>{group.colors || '-'}</span></div>
          <div className="production-work-row"><strong>Maquina:</strong> <span>Offset principal</span></div>
        </div>

        <div className="production-work-sheet-panel production-work-diagram-panel">
          <div className="production-work-diagram-label">Diagrama de Pliego</div>
          {config && pieces.length > 0 ? (
            <div className="production-work-imposition-preview">
              <SheetImposition
                sheetWidth={config.sheetWidth}
                sheetHeight={config.sheetHeight}
                gripperTop={config.gripperTop}
                gripperBottom={config.gripperBottom}
                marginLeft={config.marginLeft}
                marginRight={config.marginRight}
                pieces={pieces}
                itemWidth={itemWidth}
                itemHeight={itemHeight}
                bleed={config.bleed}
                side="front"
                maxHeight={210}
              />
              {config.doubleSided && (
                <SheetImposition
                  sheetWidth={config.sheetWidth}
                  sheetHeight={config.sheetHeight}
                  gripperTop={config.gripperTop}
                  gripperBottom={config.gripperBottom}
                  marginLeft={config.marginLeft}
                  marginRight={config.marginRight}
                  pieces={pieces}
                  itemWidth={itemWidth}
                  itemHeight={itemHeight}
                  bleed={config.bleed}
                  side="back"
                  maxHeight={210}
                />
              )}
            </div>
          ) : (
            <div className="empty-state compact-empty">Sin diagrama</div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductionList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isWorker = user?.role === 'trabajador';
  const cacheKey = `production-${user?.role || 'anon'}-${user?.id || ''}`;
  const cachedData = readPageCache(cacheKey);
  const [orders, setOrders] = useState(cachedData?.orders || []);
  const [workers, setWorkers] = useState(cachedData?.workers || []);
  const [edits, setEdits] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailTab, setDetailTab] = useState('detalle');
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [activeStatus, setActiveStatus] = useState(DEFAULT_PRODUCTION_STATUS);
  const [search, setSearch] = useState('');
  const [workerFilter, setWorkerFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [loading, setLoading] = useState(!cachedData);
  const [savingId, setSavingId] = useState('');
  const [error, setError] = useState('');
  const workersRef = useRef(workers);

  useEffect(() => {
    workersRef.current = workers;
  }, [workers]);

  const load = useCallback(async () => {
    try {
      setError('');
      const requests = [api.get('/production-orders')];
      if (!isWorker) requests.push(api.get('/workers'));
      const [ordersRes, workersRes] = await Promise.all(requests);
      setOrders(ordersRes.data || []);
      if (workersRes) setWorkers(workersRes.data || []);
      writePageCache(cacheKey, {
        orders: ordersRes.data || [],
        workers: workersRes?.data || workersRef.current,
      });
    } catch (err) {
      setError('Error al cargar produccion');
    } finally {
      setLoading(false);
    }
  }, [cacheKey, isWorker]);

  const loadSilent = useCallback(async () => {
    try {
      const { data } = await api.get('/production-orders');
      setOrders(data || []);
      writePageCache(cacheKey, {
        orders: data || [],
        workers: workersRef.current,
      });
    } catch (err) {
      // Keep current list if a background refresh fails.
    }
  }, [cacheKey]);

  useEffect(() => {
    load();
    const interval = setInterval(loadSilent, 15000);
    return () => clearInterval(interval);
  }, [load, loadSilent]);

  useRefreshOnFocus(loadSilent);

  const replaceOrder = (order) => {
    setOrders(current => current.map(item => item.id === order.id ? order : item));
    setSelectedOrder(current => current?.id === order.id ? order : current);
  };

  const loadActivity = useCallback(async (orderId) => {
    try {
      setActivityLoading(true);
      setActivityError('');
      const { data } = await api.get(`/production-orders/${orderId}/activity`);
      setActivity(data || []);
    } catch (err) {
      setActivityError('Error al cargar actividad');
      setActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const openDetail = useCallback((order) => {
    navigate(`/produccion/${order.id}`);
  }, [navigate]);

  useEffect(() => {
    const orderId = new URLSearchParams(location.search).get('order');
    if (!orderId) return;
    navigate(`/produccion/${orderId}`, { replace: true });
  }, [location.search, navigate]);

  const updateEdit = (orderId, patch) => {
    setEdits(current => ({
      ...current,
      [orderId]: { ...(current[orderId] || {}), ...patch },
    }));
  };

  const getPatch = (order) => ({
    assigned_worker_id: order.assigned_worker_id || '',
    delivery_date: order.delivery_date || '',
    production_notes: order.production_notes || '',
    priority: order.priority || DEFAULT_PRODUCTION_PRIORITY,
    ...(edits[order.id] || {}),
  });

  const saveAdminChanges = async (order) => {
    try {
      setSavingId(order.id);
      const patch = getPatch(order);
      if (!patch.assigned_worker_id) {
        alert('Selecciona un trabajador para esta orden');
        return;
      }
      const { data } = await api.put(`/production-orders/${order.id}`, patch);
      replaceOrder(data);
      setEdits(current => ({ ...current, [order.id]: {} }));
      if (selectedOrder?.id === order.id) loadActivity(order.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al guardar orden');
    } finally {
      setSavingId('');
    }
  };

  const assignWorker = async (order, workerId) => {
    if (!workerId) {
      alert('Selecciona un trabajador para esta orden');
      return;
    }
    updateEdit(order.id, { assigned_worker_id: workerId });
    try {
      setSavingId(order.id);
      const { data } = await api.put(`/production-orders/${order.id}`, {
        assigned_worker_id: workerId,
      });
      replaceOrder(data);
      setEdits(current => ({ ...current, [order.id]: { ...(current[order.id] || {}), assigned_worker_id: data.assigned_worker_id || '' } }));
      if (selectedOrder?.id === order.id) loadActivity(order.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al asignar trabajador');
      setEdits(current => ({ ...current, [order.id]: { ...(current[order.id] || {}), assigned_worker_id: order.assigned_worker_id || '' } }));
    } finally {
      setSavingId('');
    }
  };

  const changeStatus = async (order, status) => {
    try {
      setSavingId(order.id);
      const { data } = await api.put(`/production-orders/${order.id}/status`, { status });
      replaceOrder(data);
      if (selectedOrder?.id === order.id) loadActivity(order.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al cambiar estado');
    } finally {
      setSavingId('');
    }
  };

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    return orders.filter(order => {
      const statusMatches = (order.status || DEFAULT_PRODUCTION_STATUS) === activeStatus;
      const workerMatches = !workerFilter || order.assigned_worker_id === workerFilter;
      const priorityMatches = !priorityFilter || (order.priority || DEFAULT_PRODUCTION_PRIORITY) === priorityFilter;
      const searchable = [
        String(order.order_number || ''),
        String(order.budget_number || ''),
        order.client?.name || '',
        order.product?.name || '',
      ].join(' ').toLowerCase();
      const searchMatches = !query || searchable.includes(query);
      return statusMatches && workerMatches && priorityMatches && searchMatches;
    }).sort((a, b) => {
      const priorityDiff = (PRODUCTION_PRIORITY_ORDER[a.priority || DEFAULT_PRODUCTION_PRIORITY] ?? 99)
        - (PRODUCTION_PRIORITY_ORDER[b.priority || DEFAULT_PRODUCTION_PRIORITY] ?? 99);
      if (priorityDiff !== 0) return priorityDiff;
      const aDelivery = a.delivery_date ? new Date(a.delivery_date).getTime() : Number.POSITIVE_INFINITY;
      const bDelivery = b.delivery_date ? new Date(b.delivery_date).getTime() : Number.POSITIVE_INFINITY;
      if (aDelivery !== bDelivery) return aDelivery - bDelivery;
      return Number(b.order_number || 0) - Number(a.order_number || 0);
    });
  }, [orders, activeStatus, search, workerFilter, priorityFilter]);

  const statusCounts = useMemo(() => (
    orders.reduce((acc, order) => {
      const key = order.status || DEFAULT_PRODUCTION_STATUS;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, PRODUCTION_STATUSES.reduce((acc, status) => {
      acc[status.key] = 0;
      return acc;
    }, {}))
  ), [orders]);

  const renderOrderCard = (order) => {
    const image = order.design_file_url || order.product?.image_url;
    const nextStatus = NEXT_PRODUCTION_STATUS[order.status || DEFAULT_PRODUCTION_STATUS];
    const priority = order.priority || DEFAULT_PRODUCTION_PRIORITY;
    return (
      <div key={order.id} className="production-board-card" data-testid={`production-order-${order.id}`}>
        <div className="production-card-top">
          <div>
            <h3>Orden <span className="mono">#{String(order.order_number).padStart(4, '0')}</span></h3>
            <p>Presupuesto #{String(order.budget_number || '').padStart(4, '0')}</p>
          </div>
          <div className="production-card-badges">
            <span className={`production-priority production-priority-${priority}`}>
              {PRODUCTION_PRIORITY_LABELS[priority] || priority}
            </span>
            <span className={`production-status production-status-${order.status}`}>
              {PRODUCTION_STATUS_LABELS[order.status] || order.status}
            </span>
          </div>
        </div>

        <div className="production-mini-body">
          <div className="production-mini-image">
            {image ? <img src={image} alt={order.product?.name || 'Diseno'} /> : <span>Sin diseno</span>}
          </div>
          <div className="production-info compact">
            <div><strong>Cliente:</strong> {order.client?.name || '-'}</div>
            <div><strong>Producto:</strong> {order.product?.name || '-'}</div>
            <div><strong>Cantidad:</strong> <span className="mono">{formatNumber(order.quantity)}</span></div>
            <div><strong>Entrega:</strong> {formatDate(order.delivery_date)}</div>
            {!isWorker && <div><strong>Trabajador:</strong> {order.assigned_worker_name || '-'}</div>}
          </div>
        </div>

        <div className="production-actions">
          <button className="btn-secondary" onClick={() => openDetail(order)}>
            Ver
          </button>
          {nextStatus && (
            <button
              className="btn-primary-inline production-next-status"
              onClick={() => changeStatus(order, nextStatus)}
              disabled={savingId === order.id}
            >
              A {PRODUCTION_STATUS_LABELS[nextStatus]}
            </button>
          )}
        </div>
      </div>
    );
  };

  const detailOrder = selectedOrder;
  const detailPatch = detailOrder ? getPatch(detailOrder) : {};
  const detailImage = detailOrder?.design_file_url || detailOrder?.product?.image_url;
  const detailImpositionGroups = getProductionImpositionGroups(detailOrder);

  return (
    <div className="dashboard-container">
      <Sidebar
        activePath={location.pathname}
        productionStatus={activeStatus}
        onProductionStatusChange={setActiveStatus}
        productionCounts={statusCounts}
      />

      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1 data-testid="production-title">Produccion</h1>
            <p className="dashboard-subtitle">
              {PRODUCTION_STATUS_LABELS[activeStatus]} ({filteredOrders.length})
            </p>
          </div>
          <button className="btn-secondary" onClick={load} disabled={loading}>
            Actualizar
          </button>
        </div>

        {error && <div className="login-error">{error}</div>}

        <div className="card production-filters-card">
          <div className={`production-filters-grid ${isWorker ? 'worker' : ''}`}>
            <div className="form-group">
              <label className="form-label">Buscar</label>
              <input
                className="form-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Orden, cliente o producto"
              />
            </div>
            {!isWorker && (
              <div className="form-group">
                <label className="form-label">Trabajador</label>
                <select
                  className="form-input"
                  value={workerFilter}
                  onChange={(e) => setWorkerFilter(e.target.value)}
                >
                  <option value="">Todos</option>
                  {workers.map(worker => (
                    <option key={worker.id} value={worker.id}>{worker.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Prioridad</label>
              <select
                className="form-input"
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <option value="">Todas</option>
                {PRODUCTION_PRIORITIES.map(priority => (
                  <option key={priority.key} value={priority.key}>{priority.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="empty-state">Cargando ordenes...</div>
        ) : orders.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              {isWorker ? 'No hay trabajos cargados.' : 'Todavia no hay ordenes de produccion.'}
            </div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              No hay trabajos con los filtros seleccionados.
            </div>
          </div>
        ) : (
          <div className="production-grid">
            {filteredOrders.map(renderOrderCard)}
          </div>
        )}

        {detailOrder && (
          <div className="modal-backdrop" onClick={() => setSelectedOrder(null)}>
            <div className="modal production-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h3>Orden #{String(detailOrder.order_number).padStart(4, '0')}</h3>
                  <p className="dashboard-subtitle">
                    Presupuesto #{String(detailOrder.budget_number || '').padStart(4, '0')} - {PRODUCTION_STATUS_LABELS[detailOrder.status]}
                  </p>
                </div>
                <button className="btn-remove" onClick={() => setSelectedOrder(null)}>x</button>
              </div>

              <div className="modal-body">
                <div className="production-detail-tabs">
                  <button
                    className={`production-detail-tab ${detailTab === 'detalle' ? 'active' : ''}`}
                    onClick={() => setDetailTab('detalle')}
                  >
                    Detalle
                  </button>
                  <button
                    className={`production-detail-tab ${detailTab === 'actividad' ? 'active' : ''}`}
                    onClick={() => {
                      setDetailTab('actividad');
                      loadActivity(detailOrder.id);
                    }}
                  >
                    Actividad
                  </button>
                </div>

                {detailTab === 'detalle' ? (
                  <>
                    <div className="production-detail-grid">
                      <div className="production-image large">
                        {detailImage ? <img src={detailImage} alt={detailOrder.product?.name || 'Diseno'} /> : <span>Sin diseno</span>}
                      </div>
                      <div className="production-info">
                        <div><strong>Cliente:</strong> {detailOrder.client?.name || '-'}</div>
                        <div><strong>Producto:</strong> {detailOrder.product?.name || '-'}</div>
                        <div><strong>Cantidad:</strong> <span className="mono">{formatNumber(detailOrder.quantity)}</span></div>
                        <div><strong>Entrega:</strong> {formatDate(detailOrder.delivery_date)}</div>
                        <div><strong>Prioridad:</strong> {PRODUCTION_PRIORITY_LABELS[detailOrder.priority || DEFAULT_PRODUCTION_PRIORITY]}</div>
                        <div><strong>Trabajador:</strong> {detailOrder.assigned_worker_name || 'Sin asignar'}</div>
                        {detailOrder.finished_by && <div><strong>Finalizado por:</strong> {detailOrder.finished_by}</div>}
                        {detailOrder.delivered_by && <div><strong>Entregado por:</strong> {detailOrder.delivered_by}</div>}
                        {!isWorker && (
                          <div>
                            <Link className="btn-secondary" to={`/presupuestos/${detailOrder.budget_id}`}>
                              Ver presupuesto
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="production-section production-detail-edit">
                      <div className="form-label">Gestion de orden</div>
                      <div className="production-edit-grid">
                        {!isWorker && (
                          <>
                            <div className="form-group">
                              <label className="form-label">Trabajador</label>
                              <select
                                className="form-input"
                                value={detailPatch.assigned_worker_id || ''}
                                onChange={(e) => assignWorker(detailOrder, e.target.value)}
                                disabled={savingId === detailOrder.id}
                              >
                                <option value="">Seleccionar trabajador</option>
                                {workers.map(worker => (
                                  <option key={worker.id} value={worker.id}>{worker.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label className="form-label">Fecha de entrega</label>
                              <input
                                type="date"
                                className="form-input"
                                value={detailPatch.delivery_date || ''}
                                onChange={(e) => updateEdit(detailOrder.id, { delivery_date: e.target.value })}
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Prioridad</label>
                              <select
                                className="form-input"
                                value={detailPatch.priority || DEFAULT_PRODUCTION_PRIORITY}
                                onChange={(e) => updateEdit(detailOrder.id, { priority: e.target.value })}
                              >
                                {PRODUCTION_PRIORITIES.map(priority => (
                                  <option key={priority.key} value={priority.key}>{priority.label}</option>
                                ))}
                              </select>
                            </div>
                          </>
                        )}
                        <div className="form-group">
                          <label className="form-label">Estado</label>
                          <select
                            className="form-input"
                            value={detailOrder.status}
                            onChange={(e) => changeStatus(detailOrder, e.target.value)}
                            disabled={savingId === detailOrder.id}
                          >
                            {PRODUCTION_STATUSES.map(status => (
                              <option key={status.key} value={status.key}>{status.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {!isWorker && (
                        <>
                          <div className="form-group">
                            <label className="form-label">Observaciones de produccion</label>
                            <textarea
                              className="form-input"
                              rows="3"
                              value={detailPatch.production_notes || ''}
                              onChange={(e) => updateEdit(detailOrder.id, { production_notes: e.target.value })}
                            />
                          </div>
                          <button
                            className="btn-primary-inline"
                            onClick={() => saveAdminChanges(detailOrder)}
                            disabled={savingId === detailOrder.id}
                          >
                            {savingId === detailOrder.id ? 'Guardando...' : 'Guardar cambios'}
                          </button>
                        </>
                      )}
                    </div>

                    <div className="production-section">
                      <div className="form-label">Observaciones de produccion</div>
                      <p className="production-notes">{detailPatch.production_notes || detailOrder.production_notes || '-'}</p>
                    </div>

                    <div className="production-section">
                      <div className="form-label">Orden interna e imposicion</div>
                      {detailImpositionGroups.length > 0 ? (
                        <div className="production-work-sheets">
                          {detailImpositionGroups.map((group, index) => (
                            <ProductionImpositionGroup
                              key={group.key || index}
                              group={group}
                              index={index}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="empty-state compact-empty">Sin imposicion registrada</div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="activity-timeline">
                    {activityError && <div className="login-error">{activityError}</div>}
                    {activityLoading ? (
                      <div className="empty-state">Cargando actividad...</div>
                    ) : activity.length === 0 ? (
                      <div className="empty-state">Todavia no hay actividad registrada.</div>
                    ) : (
                      activity.map(entry => (
                        <div key={entry.id} className="activity-entry">
                          <div className="activity-dot"></div>
                          <div className="activity-content">
                            <div className="activity-meta">
                              <span className="mono">{formatDateTime(entry.created_at)}</span>
                              <span>{entry.actor_name || 'Sin usuario'}</span>
                              <span>{entry.actor_role || '-'}</span>
                            </div>
                            <p>{entry.description}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProductionList;
